import { useState, useRef } from "react";
import { toast } from "sonner";
import { Sparkles, PenLine, Image as ImageIcon, Loader2, Copy, Upload, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead, PremiumBanner } from "@/components/kit";

export default function AIStudio() {
  const { guildId, guild } = useGuild();
  const locked = (guild?.plan || "free") === "free";
  const [tab, setTab] = useState("write");
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("engaging");
  const [result, setResult] = useState("");
  const [image, setImage] = useState("");
  const [refImage, setRefImage] = useState("");
  const [quality, setQuality] = useState("high");
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const onPickRef = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4MB");
    const reader = new FileReader();
    reader.onload = () => setRefImage(reader.result);
    reader.readAsDataURL(f);
  };
  const clearRef = () => { setRefImage(""); if (fileRef.current) fileRef.current.value = ""; };

  const write = async () => {
    if (!prompt) return toast.error("Enter a prompt");
    setLoading(true); setResult("");
    try { const { data } = await api.post(`/guilds/${guildId}/ai/write`, { prompt, tone }); setResult(data.text); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const imagine = async () => {
    if (!prompt) return toast.error("Enter a prompt");
    setLoading(true); setImage("");
    try { const { data } = await api.post(`/guilds/${guildId}/ai/imagine`, { prompt, reference_image: refImage || undefined, quality }); setImage(data.image); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHead title="AI Studio" desc="Generate announcements, replies and images with AI." />
      <PremiumBanner guildId={guildId} tier="Premium" locked={locked} />
      <div className="inline-flex bg-zinc-900 p-1 rounded-lg mb-6">
        <button data-testid="ai-tab-write" onClick={() => setTab("write")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === "write" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><PenLine className="w-4 h-4 inline mr-1" /> Write</button>
        <button data-testid="ai-tab-imagine" onClick={() => setTab("imagine")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === "imagine" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><ImageIcon className="w-4 h-4 inline mr-1" /> Imagine</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 font-head font-semibold"><Sparkles className="w-4 h-4 text-volt" /> Prompt</div>
          <textarea data-testid="ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} placeholder={tab === "write" ? "Write a welcome announcement for a gaming server…" : "A neon cyberpunk mascot logo for a Discord gaming server"} className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm" />
          {tab === "write" && (
            <div><label className="text-xs uppercase tracking-wider text-zinc-500">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt">
                <option value="engaging">Engaging</option><option value="hype">Hype</option><option value="professional">Professional</option><option value="funny">Funny</option>
              </select></div>
          )}
          {tab === "imagine" && (
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 block mb-2">Quality</label>
              <div className="flex gap-2 mb-4" data-testid="ai-quality">
                <button type="button" data-testid="ai-quality-high" onClick={() => setQuality("high")}
                  className={`flex-1 rounded-md py-2 text-sm font-medium border ${quality === "high" ? "bg-volt/10 border-volt/60 text-volt" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
                  High <span className="text-[11px] opacity-70">best quality · uses credits</span>
                </button>
                <button type="button" data-testid="ai-quality-fast" onClick={() => setQuality("fast")}
                  className={`flex-1 rounded-md py-2 text-sm font-medium border ${quality === "fast" ? "bg-volt/10 border-volt/60 text-volt" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
                  Fast <span className="text-[11px] opacity-70">free · lower detail</span>
                </button>
              </div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 block mb-2">Reference image <span className="text-zinc-600 normal-case">(optional — we'll edit / build on it)</span></label>
              <input ref={fileRef} data-testid="ai-ref-input" type="file" accept="image/*" onChange={onPickRef} className="hidden" />
              {refImage ? (
                <div className="mt-2 relative inline-block">
                  <img data-testid="ai-ref-preview" src={refImage} alt="reference" className="h-28 rounded-md border border-zinc-800 object-cover" />
                  <button data-testid="ai-ref-clear" onClick={clearRef} className="absolute -top-2 -right-2 bg-zinc-800 border border-zinc-700 rounded-full p-1 text-zinc-300 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button type="button" data-testid="ai-ref-upload" onClick={() => fileRef.current?.click()} className="mt-2 w-full border border-dashed border-zinc-700 rounded-md py-4 text-sm text-zinc-400 hover:border-volt hover:text-volt flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4" /> Upload a reference image
                </button>
              )}
            </div>
          )}
          <button data-testid="ai-generate" onClick={tab === "write" ? write : imagine} disabled={loading} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2.5 hover:bg-volt-hover flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {tab === "write" ? "Generate text" : (refImage ? "Edit image" : "Generate image")}
          </button>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="font-head font-semibold mb-4">Result</div>
          {tab === "write" ? (
            result ? (
              <div className="relative">
                <pre data-testid="ai-result" className="whitespace-pre-wrap text-sm text-zinc-200 bg-black rounded-md p-4 font-sans">{result}</pre>
                <button onClick={() => { navigator.clipboard.writeText(result); toast.success("Copied"); }} className="absolute top-2 right-2 text-zinc-500 hover:text-white"><Copy className="w-4 h-4" /></button>
              </div>
            ) : <p className="text-zinc-600 text-sm">Generated text will appear here.</p>
          ) : (
            image ? <img data-testid="ai-image" src={image} alt="AI" className="w-full rounded-lg border border-zinc-800" /> : <p className="text-zinc-600 text-sm">Generated image will appear here.</p>
          )}
        </div>
      </div>
    </div>
  );
}
